import * as React from 'react'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { RepeatableSection } from '../components/RepeatableSection'
import { CurrencyModel } from '@/app/models/newDelegation/currencyModel'

import { BelgiumMinimumWageExpandableItemModel } from '@/app/models/configuration/minimumWage/belgiumMinimumWageConfigurationModel'
import { DriverQualificationsType } from '@/app/constants/driverQualificationsType'

import { getDriverQualificationTypeName } from '@/app/common/converters/driverQualificationToTextConverter'
import { getDriverWorkYearsPeriodsWorkName } from 'app/common/converters/configuration/driverWorkYearsPeriodsToTextConverter'
import { DriverWorkYearsPeriods } from '@/app/constants/configuration/driverWorkYearsPeriods'
import { getDriverQualificationType } from '@/app/services/contract/driverQualificationTypeResolver'

type Props = {
  allowedCurrencies?: string[]
  state: {
    configuration: { currencies: CurrencyModel[] }
    expandableItems: BelgiumMinimumWageExpandableItemModel[]
  }
  setState: React.Dispatch<
    React.SetStateAction<{
      expandableItems: BelgiumMinimumWageExpandableItemModel[]
    }>
  >
}

const QUALIFICATION_TYPES_AUSTRIA = [
  DriverQualificationsType.NotSet,
  DriverQualificationsType.VehiclesTo3Comma5Standard,
  DriverQualificationsType.VehiclesAbove3Comma5Below3Axis,
  DriverQualificationsType.VehiclesAbove3Comma5Abov3Axis,
  DriverQualificationsType.VehicleCombination,
  DriverQualificationsType.DangerousGoods,
  DriverQualificationsType.EmployeesWithSpecialistQualifications,
  DriverQualificationsType.ProfessionalDriverVehiclesAbove3Comma5Below3Axis,
  DriverQualificationsType.ProfessionalDriverVehiclesAbove3Comma5Abov3Axis,
  DriverQualificationsType.ProfessionalDriverVehicleCombination,
  DriverQualificationsType.TankDriver,
  DriverQualificationsType.TransportOfPeopleDriver,
  DriverQualificationsType.TransportOfPeopleProfessionalDriver,
  DriverQualificationsType.TransportOfPeopleQualifiedWorker,
  DriverQualificationsType.TransportOfPeopleSemiSkilledWorker,
  DriverQualificationsType.TransportOfPeopleForeman,
  DriverQualificationsType.TransportOfPeopleGarageWorker,
]

const DRIVER_WORK_YEARS_PERIODS = [
  DriverWorkYearsPeriods.All,
  DriverWorkYearsPeriods.None,
  DriverWorkYearsPeriods.Below5,
  DriverWorkYearsPeriods.Below1,
  DriverWorkYearsPeriods.Above2Below10,
  DriverWorkYearsPeriods.Above5Below10,
  DriverWorkYearsPeriods.Above10Below15,
  DriverWorkYearsPeriods.Above11Below20,
  DriverWorkYearsPeriods.Above15Below20,
  DriverWorkYearsPeriods.Above20,
  DriverWorkYearsPeriods.Above21,
  DriverWorkYearsPeriods.Below4,
  DriverWorkYearsPeriods.Above4Below8,
  DriverWorkYearsPeriods.Above8Below12,
  DriverWorkYearsPeriods.Above12,
]

export const AustriaMinimumWageExpandableItemSection = ({
  allowedCurrencies,
  state,
  setState,
}: Props) => {
  const { t } = useLocalize()

  return (
    <RepeatableSection<BelgiumMinimumWageExpandableItemModel>
      title={t('configuration.minimumWages.minimalHourRateTitle')}
      newRow={() => ({
        configurationValueId: 0,
        rateValue: 0,
        dateFrom: moment(),
        driverYears: 0,
        rateCurrency: state.configuration.currencies[0],
        driverQualifications: DriverQualificationsType.Guard,
        isNew: true,
        isEdited: true,
      })}
      columns={[
        {
          order: 1,
          label: t('configuration-minimumWage.from-date'),
          type: 'date',
          get: (r) => r.dateFrom,
          set: (r, v) => ({ ...r, dateFrom: moment(v) }),
        },
        {
          order: 3,
          label: t('common.type'),
          type: 'select',
          options: QUALIFICATION_TYPES_AUSTRIA.map((value) => ({
            value,
            label: getDriverQualificationTypeName(value, t),
          })),
          get: (r) =>
            typeof r.driverQualifications === 'number'
              ? getDriverQualificationType(r.driverQualifications)
              : r.driverQualifications,

          set: (r, v: DriverQualificationsType) => ({
            ...r,
            driverQualifications: v,
          }),
        },
        {
          order: 4,
          label: t('common.amount'),
          type: 'price',
          get: (r) => r.rateValue,
          set: (r, v: number) => ({ ...r, rateValue: Number(v) }),
        },
        {
          order: 5,
          label: t('configuration.countries.currency'),
          type: 'select', //"currency",
          options: state.configuration.currencies
            .filter((c) =>
              Array.isArray(allowedCurrencies)
                ? allowedCurrencies.includes(c.code)
                : true,
            )
            .map((c) => ({ label: `${c.code} - ${c.name}`, value: c.code }))
            .sort((a, b) => a.label.localeCompare(b.label)),
          get: (r) => r.rateCurrency.code,
          set: (r, v) => {
            const selected = state.configuration.currencies.find(
              (c) => c.code === v,
            )
            return { ...r, rateCurrency: selected }
          },
        },
        {
          order: 6,
          label: t('configuration.minimumWages.driverWorkYears'),
          type: 'select',
          options: DRIVER_WORK_YEARS_PERIODS.map((value) => ({
            value: value.toString(),
            label: getDriverWorkYearsPeriodsWorkName(value, t),
          })),
          get: (r) => r.driverYears,
          set: (r, v) => {
            return { ...r, driverYears: v as DriverWorkYearsPeriods }
          },
        },
      ]}
      canAddRow={true}
      rows={state.expandableItems}
      setRows={(next) => {
        setState((prev) => {
          const current = prev.expandableItems
          const nextRows =
            typeof next === 'function'
              ? (next as (p: typeof current) => typeof current)(current)
              : next
          return { ...prev, expandableItems: nextRows }
        })
      }}
      sortRows={(a, b) => {
        const da = moment(a.dateFrom)
        const db = moment(b.dateFrom)
        const ta = da.valueOf()
        const tb = db.valueOf()
        if (ta === tb) return 0
        return ta > tb ? -1 : 1
      }}
    />
  )
}
